/**
 * MindoraCharacter — persistent mascot component
 * Uses the existing chat_empty.png robot mascot with mood-based animations
 * Supports 9 moods, mood transitions, and reduced-motion accessibility
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useCompanionSound } from '../../hooks/useCompanionSound';

export type MindoraMood =
  | 'idle' | 'happy' | 'thinking' | 'celebrating'
  | 'waving' | 'sleeping' | 'encouraging' | 'waiting' | 'surprised';
export type MindoraSize = 'sm' | 'md' | 'lg' | 'xl';

interface MindoraCharacterProps {
  mood?: MindoraMood;
  size?: MindoraSize;
  animate?: boolean;
  className?: string;
}

const sizeMap: Record<MindoraSize, number> = {
  sm: 40,
  md: 64,
  lg: 96,
  xl: 192,
};

const moodAnimations: Record<MindoraMood, object> = {
  idle: {
    y: [0, -6, 0],
    transition: { repeat: Infinity, duration: 2.5, ease: 'easeInOut' },
  },
  happy: {
    y: [0, -10, 0],
    rotate: [0, 5, -5, 0],
    scale: [1, 1.1, 1],
    transition: { repeat: Infinity, duration: 1.5, ease: 'easeInOut' },
  },
  thinking: {
    rotate: [0, -8, 0],
    transition: { repeat: Infinity, duration: 2, ease: 'easeInOut' },
  },
  celebrating: {
    y: [0, -16, 0],
    scale: [1, 1.15, 1],
    rotate: [0, 8, -8, 0],
    transition: { repeat: Infinity, duration: 1, ease: 'easeInOut' },
  },
  waving: {
    rotate: [0, 14, -8, 14, 0],
    transition: { repeat: Infinity, duration: 1.8, ease: 'easeInOut' },
  },
  sleeping: {
    y: [0, 3, 0],
    rotate: [0, -3, 0],
    transition: { repeat: Infinity, duration: 3, ease: 'easeInOut' },
  },
  encouraging: {
    y: [0, -4, 0],
    rotate: [0, 3, -2, 3, 0],
    scale: [1, 1.05, 1],
    transition: { repeat: Infinity, duration: 2, ease: 'easeInOut' },
  },
  waiting: {
    y: [0, -2, 0, -2, 0],
    rotate: [0, -1, 0, 1, 0],
    transition: { repeat: Infinity, duration: 3.5, ease: 'easeInOut' },
  },
  surprised: {
    scale: [1, 1.2, 1.05, 1],
    y: [0, -12, -4, 0],
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

// Mood overlays
function MoodOverlay({ mood, size }: { mood: MindoraMood; size: number }) {
  const overlaySize = Math.max(size * 0.3, 14);

  if (mood === 'celebrating') {
    return (
      <motion.span
        className="absolute -top-1 -right-1 pointer-events-none"
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 3, ease: 'linear' }}
        style={{ fontSize: overlaySize }}
      >
        ⭐
      </motion.span>
    );
  }

  if (mood === 'thinking') {
    return (
      <motion.span
        className="absolute -top-2 -right-1 pointer-events-none"
        animate={{ opacity: [0.5, 1, 0.5], y: [0, -4, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        style={{ fontSize: overlaySize }}
      >
        💭
      </motion.span>
    );
  }

  if (mood === 'sleeping') {
    return (
      <motion.span
        className="absolute -top-2 -right-0 pointer-events-none font-bold text-kid-violet"
        animate={{ opacity: [0, 1, 0], y: [0, -8, -16], scale: [0.6, 1, 0.6] }}
        transition={{ repeat: Infinity, duration: 2.5 }}
        style={{ fontSize: overlaySize }}
      >
        Z
      </motion.span>
    );
  }

  if (mood === 'encouraging') {
    return (
      <motion.span
        className="absolute -top-1 -right-1 pointer-events-none"
        animate={{ rotate: [0, 15, -10, 15, 0] }}
        transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
        style={{ fontSize: overlaySize }}
      >
        👍
      </motion.span>
    );
  }

  if (mood === 'waiting') {
    return (
      <motion.span
        className="absolute -top-1 -right-0 pointer-events-none font-bold text-kid-text-soft"
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ repeat: Infinity, duration: 1.5 }}
        style={{ fontSize: overlaySize * 0.8 }}
      >
        ...
      </motion.span>
    );
  }

  if (mood === 'surprised') {
    return (
      <motion.span
        className="absolute -top-2 -right-1 pointer-events-none text-kid-coral font-bold"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 500, damping: 15 }}
        style={{ fontSize: overlaySize }}
      >
        ❗
      </motion.span>
    );
  }

  return null;
}

export default function MindoraCharacter({
  mood = 'idle',
  size = 'md',
  animate = true,
  className = '',
}: MindoraCharacterProps) {
  const px = sizeMap[size];
  const reduced = useReducedMotion();
  const { playForMood } = useCompanionSound();
  const [currentMood, setCurrentMood] = useState(mood);
  const [transitioning, setTransitioning] = useState(false);
  const prevMoodRef = useRef(mood);
  const hasMountedRef = useRef(false);

  // Mood transition: squish and pop + companion sound
  useEffect(() => {
    if (mood !== prevMoodRef.current && animate && !reduced) {
      // Play companion sound only after initial mount
      if (hasMountedRef.current) {
        playForMood(mood);
      }
      setTransitioning(true);
      const timer = setTimeout(() => {
        setCurrentMood(mood);
        setTransitioning(false);
      }, 150);
      prevMoodRef.current = mood;
      return () => clearTimeout(timer);
    }
    setCurrentMood(mood);
    prevMoodRef.current = mood;
    hasMountedRef.current = true;
  }, [mood, animate, reduced, playForMood]);

  const shouldAnimate = animate && !reduced;

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`}>
      <AnimatePresence mode="wait">
        <motion.img
          key={currentMood}
          src="/chat_empty.png"
          alt="Mindora"
          width={px}
          height={px}
          className="object-contain"
          animate={
            shouldAnimate
              ? transitioning
                ? { scale: 0.9 }
                : (moodAnimations[currentMood] as any)
              : undefined
          }
          transition={
            transitioning
              ? { duration: 0.15 }
              : undefined
          }
          draggable={false}
        />
      </AnimatePresence>
      {shouldAnimate && !transitioning && <MoodOverlay mood={currentMood} size={px} />}
    </div>
  );
}
